import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Camera, CheckCircle2, ChevronDown, ChevronRight, Download, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { dashboardApi, CameraSetup } from '@/lib/api';
import { sortByNatural } from '@/lib/utils';

/**
 * Camera inventory (1.32.0, admin). The agent reads each idle sheet's OBS camera
 * setup every hour; this shows, per sheet, what OBS has now, the last setup seen
 * with every camera receiving video ("last known good"), and the history.
 * It's the record a rebuilt or mis-cloned OBS is put back from — Frogtown's
 * rebuild (2026-10-20) had nothing to go on but one correctly set-up sheet.
 */

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

function download(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function SetupTable({ setup, showReceiving }: { setup: CameraSetup; showReceiving: boolean }) {
  return (
    <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">OBS input</th>
            <th className="px-2 py-1.5 text-left font-medium">NDI source</th>
            {showReceiving && <th className="px-2 py-1.5 text-left font-medium">Video</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {sortByNatural(setup.inputs, i => i.inputName).map(i => (
            <tr key={i.inputName} className="text-gray-900 dark:text-gray-100">
              <td className="whitespace-nowrap px-2 py-1.5 font-medium">{i.inputName}</td>
              <td className="px-2 py-1.5 font-mono">{String(i.settings['ndi_source_name'] ?? '') || <span className="italic text-gray-400">(none)</span>}</td>
              {showReceiving && (
                <td className="px-2 py-1.5">
                  {i.receiving
                    ? <span className="text-green-700 dark:text-green-400">receiving</span>
                    : <span className="text-amber-700 dark:text-amber-400">no video</span>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 border-t border-gray-100 px-2 py-1.5 text-[11px] text-gray-600 dark:border-gray-800 dark:text-gray-400">
        <span>NDI output: <span className="font-mono text-gray-800 dark:text-gray-200">{setup.ndiOutputName ?? '—'}</span></span>
        <span>WebSocket port: <span className="font-mono text-gray-800 dark:text-gray-200">{setup.obsPort ?? '—'}</span></span>
        <span>Scene collection: <span className="font-mono text-gray-800 dark:text-gray-200">{setup.sceneCollection ?? '—'}</span></span>
        {setup.obsExePath && <span>OBS: <span className="font-mono text-gray-800 dark:text-gray-200">{setup.obsExePath}</span></span>}
      </div>
    </div>
  );
}

export function CameraInventoryTab({ facility }: { facility: { facilityId: string; name: string } }) {
  const [openHistory, setOpenHistory] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'site' | 'all' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['camera-inventory', facility.facilityId],
    queryFn: () => dashboardApi.getCameraInventory(facility.facilityId),
  });
  const sheets = sortByNatural(data?.sheets ?? [], s => s.sheetName);

  const doExport = async (scope: 'site' | 'all') => {
    setExporting(scope); setExportError(null);
    try {
      const json = await dashboardApi.exportCameraInventory(scope === 'site' ? facility.facilityId : undefined);
      const stamp = new Date().toISOString().slice(0, 10);
      download(json, `camera-inventory-${scope === 'site' ? facility.facilityId : 'all-sites'}-${stamp}.json`);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Export failed');
    } finally { setExporting(null); }
  };

  return (
    <div className="space-y-4 text-sm">
      <div>
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <Camera className="h-4 w-4 text-blue-500" /> Camera inventory
        </h2>
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          Every hour the agent records each idle sheet’s OBS camera inputs — the exact NDI source each one uses — plus the
          NDI output name and WebSocket port. <span className="font-medium text-gray-800 dark:text-gray-200">Last known good</span> is
          the most recent setup seen with every camera receiving video: the record to put a rebuilt or mis-copied OBS back
          from. Needs agent 1.0.38 or later.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => doExport('site')} disabled={!!exporting || sheets.length === 0}>
          {exporting === 'site' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}
          Download {facility.name} (JSON)
        </Button>
        <Button variant="outline" size="sm" onClick={() => doExport('all')} disabled={!!exporting}>
          {exporting === 'all' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}
          Download all sites (JSON)
        </Button>
        {exportError && <span className="text-xs text-red-600 dark:text-red-400">{exportError}</span>}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : isError ? (
        <p className="text-sm text-red-600 dark:text-red-400">Couldn’t load the camera inventory: {(error as Error)?.message}</p>
      ) : sheets.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          No camera snapshots yet. They appear within about an hour of this site’s agent running 1.0.38 or later.
        </div>
      ) : (
        <div className="space-y-4">
          {sheets.map(s => {
            const stale = s.lastGood && !s.currentIsLastGood;
            return (
              <div key={s.sheetName} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">Sheet {s.sheetName}</span>
                  <span className="text-xs">
                    {s.currentIsLastGood ? (
                      <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
                        <CheckCircle2 className="h-3.5 w-3.5" /> All cameras receiving — last verified {fmt(s.current.lastReceivingAt)}
                      </span>
                    ) : stale ? (
                      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="h-3.5 w-3.5" /> Current setup differs from the last known good ({fmt(s.lastGood!.lastReceivingAt)})
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="h-3.5 w-3.5" /> Not yet seen with every camera receiving
                      </span>
                    )}
                  </span>
                </div>

                <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Now in OBS · read {fmt(s.current.lastSeenAt)}
                </p>
                <SetupTable setup={s.current} showReceiving />

                {stale && s.lastGood && (
                  <>
                    <p className="mb-1 mt-3 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Last known good · verified {fmt(s.lastGood.lastReceivingAt)}
                    </p>
                    <SetupTable setup={s.lastGood} showReceiving={false} />
                  </>
                )}

                {s.history.length > 1 && (
                  <div className="mt-2">
                    <button
                      onClick={() => setOpenHistory(openHistory === s.sheetName ? null : s.sheetName)}
                      className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                    >
                      {openHistory === s.sheetName ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      History ({s.history.length} setups)
                    </button>
                    {openHistory === s.sheetName && (
                      <ul className="mt-1 space-y-1 text-xs text-gray-700 dark:text-gray-300">
                        {s.history.map(h => (
                          <li key={h.id} className="rounded bg-gray-50 px-2 py-1 dark:bg-gray-800/60">
                            <span className="text-gray-500 dark:text-gray-400">
                              {fmt(h.firstSeenAt)} → {fmt(h.lastSeenAt)}{h.lastReceivingAt ? ' · verified' : ''}:
                            </span>{' '}
                            <span className="font-mono">{h.sources.join(' · ')}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
